import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { TerrainCanvasInfo } from "@nens/deck-gl";
import { useOnlyOnce } from "@nens/react/hooks";

import { getTwoDFlowSpeedLayer } from "../../../state/layerCalculationMenu";
import { getDemRasterUrl, getSelectedModelRaster } from "../../../state/layerMenu";
import { getMap, getMapBounds } from "../../../state/map";
import { getModelExtent } from "../../../state/simulation";

import { getRegion, removeDemVizzes, resetDemVizzes } from "./terrainMain";

let loading = false;
let savedRasterUrl: string | null = null;

const useTerrainCanvas = () => {
  const [terrainCanvasInfo, setTerrainCanvasInfo] = useState<TerrainCanvasInfo | null>(null);

  const map = useSelector(getMap);
  const mapBounds = useSelector(getMapBounds);

  const extent = useSelector(getModelExtent);
  const demUrl = useSelector(getDemRasterUrl);
  const nsActive = useSelector(getSelectedModelRaster) === "dem_file";
  const twoDFlowConfig = useSelector(getTwoDFlowSpeedLayer);

  // Delete all dem vizzes when new simulation is started
  useOnlyOnce(() => {
    removeDemVizzes();
  });

  useEffect(() => {
    const createDemCanvas = async () => {
      const demActive = (twoDFlowConfig.visible && !twoDFlowConfig.disabled) || nsActive;

      if (!map || !demUrl || !extent || !mapBounds || !demActive) return;

      loading = true;

      const zoom = map.getZoom();
      const region = await getRegion(extent.coordinates, mapBounds, demUrl, zoom, nsActive);

      loading = false;

      if (!region) return;
      const { imageNS, imageDEM, newBounds } = region;

      // Apply bounds and new canvas
      setTerrainCanvasInfo({ imageNS, imageDEM, zoom, terrainBounds: newBounds });
    };
    if (savedRasterUrl && savedRasterUrl !== demUrl) resetDemVizzes();
    savedRasterUrl = demUrl;
    if (!loading) createDemCanvas();
  }, [nsActive, twoDFlowConfig, demUrl, map, mapBounds]);

  return useMemo(() => terrainCanvasInfo, [terrainCanvasInfo]);
};

export default useTerrainCanvas;
